import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { FileArrowUp, Plus } from "@phosphor-icons/react";
import { lies, schick } from "../../api/client";
import type { Besprechung, BesprechungKurz, ImportVorschau, Projekt } from "../../api/types";
import { Dialog, Feld, Meldung } from "../../components/Bausteine";
import { BESPRECHUNG_ART, datum, heute } from "../../lib/format";
import { Auswahl } from "../../components/Auswahl";

export function BesprechungenReiter({ projekt, kann }: { projekt: Projekt; kann: boolean }) {
  const liste = useQuery({
    queryKey: ["besprechungen", projekt.id],
    queryFn: () => lies<BesprechungKurz[]>(`/projekte/${projekt.id}/besprechungen`),
  });
  const [neu, setNeu] = useState(false);
  const [uebernahme, setUebernahme] = useState(false);
  const entwurf = liste.data?.find((b) => b.status === "entwurf");

  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2>Protokolle</h2>
        <span className="mute">fortgeschrieben – offene Punkte wandern in die nächste Besprechung</span>
        <div className="luft" />
        {projekt.meine_rolle === "leitung" && liste.data?.length === 0 && (
          <button className="knopf" onClick={() => setUebernahme(true)}>
            <FileArrowUp size={16} /> Bisheriges Protokoll übernehmen
          </button>
        )}
        {kann && !entwurf && (
          <button className="knopf haupt" onClick={() => setNeu(true)}>
            <Plus size={16} weight="bold" /> Nächste Besprechung
          </button>
        )}
      </div>
      <Meldung fehler={liste.error} />
      <div className="liste">
        {liste.data?.map((b) => (
          <Link key={b.id} to={`/b/${b.id}`} className="zeile">
            <span className="nr">Nr. {b.nummer}</span>
            <div className="haupt">
              <div>{b.titel || BESPRECHUNG_ART[b.art]}</div>
              <div className="mute">{datum(b.datum)}{b.ort && ` · ${b.ort}`} · {b.punkte} Punkte</div>
            </div>
            {b.status === "entwurf"
              ? <span className="chip warn">Entwurf</span>
              : <span className="chip gut">freigegeben</span>}
          </Link>
        ))}
        {liste.data?.length === 0 && (
          <div className="leer">Noch keine Besprechung. Läuft die Baustelle schon, lässt sich das letzte
            Word-Protokoll übernehmen – die nächste Besprechung schreibt es dann fort.</div>
        )}
      </div>
      <NeueBesprechung projekt={projekt} offen={neu} zu={() => setNeu(false)} />
      {uebernahme && <Uebernahme projekt={projekt} zu={() => setUebernahme(false)} />}
    </section>
  );
}

function NeueBesprechung({ projekt, offen, zu }: { projekt: Projekt; offen: boolean; zu: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [f, setF] = useState({ art: "baubesprechung", datum: heute(), beginn: "", ort: "" });
  const anlegen = useMutation({
    mutationFn: () => schick<Besprechung>(`/projekte/${projekt.id}/besprechungen`,
      { ...f, beginn: f.beginn || null }),
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ["besprechungen", projekt.id] });
      zu();
      nav(`/b/${b.id}`);
    },
  });
  return (
    <Dialog offen={offen} zu={zu} titel="Nächste Besprechung" fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={anlegen.isPending} onClick={() => anlegen.mutate()}>
        Anlegen und fortschreiben
      </button>
    </>}>
      <div className="raster">
        <Feld titel="Art">
          <Auswahl value={f.art} onChange={(e) => setF({ ...f, art: e.target.value })}>
            {Object.entries(BESPRECHUNG_ART).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Auswahl>
        </Feld>
        <Feld titel="Datum">
          <input type="date" className="eingabe" value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })} />
        </Feld>
        <Feld titel="Beginn">
          <input type="time" className="eingabe" value={f.beginn} onChange={(e) => setF({ ...f, beginn: e.target.value })} />
        </Feld>
        <Feld titel="Ort">
          <input className="eingabe" value={f.ort} placeholder="Baucontainer" onChange={(e) => setF({ ...f, ort: e.target.value })} />
        </Feld>
      </div>
      <p className="mute">Teilnehmende und alle offenen Punkte werden aus der letzten Besprechung übernommen.</p>
      <Meldung fehler={anlegen.error} />
    </Dialog>
  );
}

/**
 * Ein laufendes Projekt aus Word übernehmen (SPEC §4.6): erst lesen und zeigen, was
 * angelegt würde, dann auf Bestätigung schreiben. Die Datei geht dafür zweimal hoch —
 * sie zwischen beiden Schritten auf dem Server aufzuheben wäre ein Zustand mehr, der
 * aufgeräumt werden müsste, für ein paar hundert Kilobyte.
 */
function Uebernahme({ projekt, zu }: { projekt: Projekt; zu: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [datei, setDatei] = useState<File | null>(null);
  const form = (d: File) => { const f = new FormData(); f.append("datei", d); return f; };
  const lesen = useMutation({
    mutationFn: (d: File) => schick<ImportVorschau>(`/projekte/${projekt.id}/protokoll-import?vorschau=true`, form(d)),
  });
  const schreiben = useMutation({
    mutationFn: () => schick<Besprechung>(`/projekte/${projekt.id}/protokoll-import?vorschau=false`, form(datei!)),
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ["besprechungen", projekt.id] });
      qc.invalidateQueries({ queryKey: ["projekt", projekt.id] });
      qc.invalidateQueries({ queryKey: ["beteiligte", projekt.id] });
      zu();
      nav(`/b/${b.id}`);
    },
  });
  const v = lesen.data;
  const punkte = v?.themen.reduce((n, t) => n + t.punkte.length, 0) ?? 0;
  const offen = v?.themen.reduce((n, t) => n + t.punkte.filter((q) => q.status === "offen").length, 0) ?? 0;
  return (
    <Dialog offen zu={zu} titel="Bisheriges Protokoll übernehmen" fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!v || schreiben.isPending} onClick={() => schreiben.mutate()}>
        Übernehmen
      </button>
    </>}>
      <p className="mute" style={{ marginTop: 0 }}>
        Das letzte Protokoll als .docx – im Format der Büro-Vorlage oder als nummerierte Liste
        (Lfd.-Nr. | Thema | Erledigung durch | Datum). Blau Geschriebenes wird Vorgeschichte,
        Themen werden zur Gliederung, Teilnehmende zu Beteiligten. Ältere .doc-Dateien in Word mit
        „Speichern unter → .docx“ umspeichern.
      </p>
      <input type="file" className="eingabe" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
             onChange={(e) => { const d = e.target.files?.[0] ?? null; setDatei(d); if (d) lesen.mutate(d); }} />
      <Meldung fehler={lesen.error ?? schreiben.error} />
      {v && (
        <>
          <p><b>{v.titel || "Besprechung"} – {v.nummer ?? 1}</b> vom {datum(v.datum)}{v.ort && `, ${v.ort}`}<br />
            <span className="mute">{v.teilnahme.length} Teilnehmende · {v.themen.length} Themen · {punkte} Punkte,
              davon {offen} offen{v.naechste_am && ` · nächster Termin ${datum(v.naechste_am)}`}</span></p>
          {v.hinweise.map((h) => <div key={h} className="hinweis">{h}</div>)}
          <div className="import-themen">
            {v.themen.map((t) => (
              <div key={t.nummer}><b>{String(t.nummer).padStart(2, "0")} {t.titel}</b>{" "}
                <span className="mute">– {t.punkte.length} Punkte{t.punkte.some((q) => q.wichtig) && ", rot markiert"}</span></div>
            ))}
          </div>
          <p className="mute">Die Übernahme ist sofort freigegeben und lässt sich nicht mehr ändern.
            Korrekturen gehören in die nächste Besprechung.</p>
        </>
      )}
    </Dialog>
  );
}
